import React, { useMemo } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

/**
 * Compact sparkline-style trainability chart for player cards.
 *
 * Props:
 *   realPoints    – [{ date: "YYYY-MM-DD", trainability: number }]
 *   scenarios     – [{ run_id, name, points: [{ date, trainability }] }]   (max 3)
 *   isDarkMode    – boolean
 *   height        – chart height in px (default 80)
 *   todayDate     – "YYYY-MM-DD" string for the vertical "today" line
 */

const SCENARIO_COLORS = ["#6366f1", "#f59e0b", "#06b6d4"]; // indigo, amber, cyan

const shortDate = (d) => {
  // "YYYY-MM-DD" → "DD/MM"
  const [, m, dd] = d.split("-");
  return `${dd}/${m}`;
};

export default function TrainabilityMiniChart({
  realPoints = [],
  scenarios = [],
  isDarkMode = true,
  height = 80,
  todayDate,
}) {
  // Build a unified data array indexed by date
  const { data, hasData } = useMemo(() => {
    const byDate = new Map();

    // Real points
    for (const pt of realPoints) {
      if (!byDate.has(pt.date)) byDate.set(pt.date, { date: pt.date });
      byDate.get(pt.date).real = pt.trainability;
    }

    // Scenario points (up to 3)
    scenarios.slice(0, 3).forEach((sc, idx) => {
      for (const pt of sc.points || []) {
        if (!byDate.has(pt.date)) byDate.set(pt.date, { date: pt.date });
        byDate.get(pt.date)[`sc${idx}`] = pt.trainability;
      }
    });

    // Bridge: connect the last real point to the first predictive point
    // so lines don't gap. Copy last real value as "bridge" into pred dates.
    const sortedDates = [...byDate.keys()].sort();
    let lastRealVal = null;
    let lastRealDate = null;
    for (const d of sortedDates) {
      const entry = byDate.get(d);
      if (entry.real != null) {
        lastRealVal = entry.real;
        lastRealDate = d;
      }
    }

    // For each scenario, find its first date and inject bridge
    if (lastRealVal != null) {
      scenarios.slice(0, 3).forEach((sc, idx) => {
        const pts = (sc.points || []).filter((p) => p.date > lastRealDate);
        if (pts.length > 0) {
          const firstPredDate = pts.sort((a, b) => a.date.localeCompare(b.date))[0].date;
          // Inject the last real value as starting point of the scenario line
          // on the same date as lastRealDate if no sc value there yet
          const entry = byDate.get(lastRealDate);
          if (entry && entry[`sc${idx}`] == null) {
            entry[`sc${idx}`] = lastRealVal;
          }
        }
      });
    }

    const arr = [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
    return { data: arr, hasData: arr.length > 0 };
  }, [realPoints, scenarios]);

  if (!hasData) return null;

  const textColor = isDarkMode ? "#9ca3af" : "#6b7280";
  const gridColor = isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";

  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 4, right: 4, bottom: 0, left: -20 }}
        >
          <XAxis
            dataKey="date"
            tickFormatter={shortDate}
            tick={{ fontSize: 9, fill: textColor }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />
          <YAxis
            domain={[0, 10]}
            tick={{ fontSize: 9, fill: textColor }}
            axisLine={false}
            tickLine={false}
            ticks={[0, 5, 10]}
          />
          <Tooltip
            contentStyle={{
              background: isDarkMode ? "#1f2937" : "#fff",
              border: `1px solid ${isDarkMode ? "#374151" : "#e5e7eb"}`,
              borderRadius: 8,
              fontSize: 11,
              padding: "4px 8px",
            }}
            labelFormatter={shortDate}
            formatter={(value, name) => {
              const label =
                name === "real"
                  ? "Real"
                  : `Scenario ${Number(name.replace("sc", "")) + 1}`;
              return [value?.toFixed(1), label];
            }}
          />

          {/* Today vertical line */}
          {todayDate && (
            <ReferenceLine
              x={todayDate}
              stroke={isDarkMode ? "#4b5563" : "#d1d5db"}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}

          {/* Real trainability line */}
          <Line
            dataKey="real"
            type="monotone"
            stroke="#10b981"
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />

          {/* Scenario lines (dashed) */}
          {scenarios.slice(0, 3).map((_, idx) => (
            <Line
              key={`sc${idx}`}
              dataKey={`sc${idx}`}
              type="monotone"
              stroke={SCENARIO_COLORS[idx]}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
